import { idb } from "../../db/index.ts";
import { g, helpers, logEvent } from "../../util/index.ts";
import type { Conditions } from "../../../common/types.ts";

// Counting stats only. Rate stats like AVG and ERA need a qualification minimum that is only
// meaningful at the end of a season, so they are not checked game by game.
const COUNTING = {
	hr: "home runs",
	gsl: "grand slams",
	rbi: "RBIs",
	h: "hits",
	r: "runs",
	sb: "stolen bases",
	bb: "walks",
	"2b": "doubles",
	"3b": "triples",
	w: "wins",
	soPit: "strikeouts",
	sv: "saves",
	sho: "shutouts",
	cg: "complete games",
	pg: "perfect games",
} as const;

type Stat = keyof typeof COUNTING;
type Record_ = {
	value: number;
	pid: number | undefined;
	name: string;
	season?: number;
};
type Context = "season" | "seasonPlayoffs" | "career" | "careerPlayoffs";
type Records = builtRecords | undefined;
type builtRecords = Record<Context, Partial<Record<Stat, Record_>>>;

// Computed once per league from the full history, then kept up to date as records fall.
// Deliberately not persisted: recomputing from saved stats after a reload gives the same
// answer, so there is nothing to migrate or keep in sync.
//
// Keyed by league id. Without that, importing a second league into the same tab would keep
// the previous league's records and report nonsense - a record "broken" at a value far below
// the real one, crediting a player from the old save.
let cache: Records;
let cacheLid: number | undefined;

const empty = (): builtRecords => ({
	season: {},
	seasonPlayoffs: {},
	career: {},
	careerPlayoffs: {},
});

const consider = (
	recs: builtRecords,
	ctx: Context,
	stat: Stat,
	value: number,
	pid: number,
	name: string,
	season?: number,
) => {
	if (value <= 0) {
		return false;
	}
	const cur = recs[ctx][stat];
	if (cur === undefined || value > cur.value) {
		recs[ctx][stat] = { value, pid, name, season };
		return cur !== undefined;
	}
	return false;
};

const build = async (): Promise<builtRecords> => {
	const recs = empty();

	for await (const { value: p } of idb.league.transaction("players").store) {
		const name = `${p.firstName} ${p.lastName}`;
		const bySeason: Record<string, any> = {};
		const career: Record<string, any> = { reg: {}, po: {} };

		for (const row of p.stats ?? []) {
			const g2 = row.playoffs ? "po" : "reg";
			const key = `${g2}|${row.season}`;
			bySeason[key] ??= {};
			for (const stat of Object.keys(COUNTING) as Stat[]) {
				const v = (row as any)[stat] ?? 0;
				bySeason[key][stat] = (bySeason[key][stat] ?? 0) + v;
				career[g2][stat] = (career[g2][stat] ?? 0) + v;
			}
		}

		for (const [key, totals] of Object.entries(bySeason)) {
			const [g2, seasonStr] = key.split("|");
			const ctx: Context = g2 === "po" ? "seasonPlayoffs" : "season";
			for (const stat of Object.keys(COUNTING) as Stat[]) {
				consider(
					recs,
					ctx,
					stat,
					totals[stat] ?? 0,
					p.pid,
					name,
					Number(seasonStr),
				);
			}
		}
		for (const [g2, ctx] of [
			["reg", "career"],
			["po", "careerPlayoffs"],
		] as [string, Context][]) {
			for (const stat of Object.keys(COUNTING) as Stat[]) {
				consider(recs, ctx, stat, career[g2][stat] ?? 0, p.pid, name);
			}
		}
	}

	return recs;
};

// Stored alongside the league's own settings so the scan below only ever has to happen once per
// league, and not at all for a league file that already ships with them. That matters on a phone:
// building from scratch walks every retired player, and a league with a century of history keeps
// tens of megabytes of stats down there.
const RECORDS_KEY = "leagueRecords";

const load = async (): Promise<builtRecords | undefined> => {
	try {
		const row: any = await idb.league.get("gameAttributes", RECORDS_KEY as any);
		const value = row?.value;

		// Reject anything not shaped like records, so a corrupt value rebuilds instead of
		// silently reporting nonsense.
		if (
			value &&
			typeof value === "object" &&
			value.season &&
			value.career &&
			value.seasonPlayoffs &&
			value.careerPlayoffs
		) {
			return value as builtRecords;
		}
	} catch {
		// No stored records, or the store is unavailable - fall through to building them
	}
	return undefined;
};

const save = async (recs: builtRecords) => {
	try {
		await idb.league.put("gameAttributes", {
			key: RECORDS_KEY,
			value: recs,
		} as any);
	} catch {
		// Persisting is an optimisation, not a requirement - a failure just means the next
		// session rebuilds
	}
};

export const getRecords = async () => {
	const lid = g.get("lid");
	if (cache === undefined || cacheLid !== lid) {
		const loaded = await load();
		if (loaded) {
			cache = loaded;
		} else {
			cache = await build();
			await save(cache);
		}
		cacheLid = lid;
	}
	return cache;
};

export const clearRecordsCache = () => {
	cache = undefined;
	cacheLid = undefined;
};

const LABELS: Record<Context, string> = {
	season: "single season",
	seasonPlayoffs: "single postseason",
	career: "career",
	careerPlayoffs: "postseason career",
};

/**
 * Compare the players who just played against the league records, announcing and updating any
 * that were broken. Called after each game so a record falling is reported when it happens
 * rather than at the end of the season.
 */
const checkRecords = async (
	pids: number[],
	playoffs: boolean,
	conditions?: Conditions,
) => {
	if (pids.length === 0) {
		return;
	}

	const recs = await getRecords();
	let changed = false;
	const season = g.get("season");

	for (const pid of pids) {
		const p = await idb.cache.players.get(pid);
		if (!p) {
			continue;
		}
		const name = `${p.firstName} ${p.lastName}`;

		const seasonTotals: Record<string, number> = {};
		const careerTotals: Record<string, number> = {};
		for (const row of p.stats ?? []) {
			if (!!row.playoffs !== playoffs) {
				continue;
			}
			for (const stat of Object.keys(COUNTING) as Stat[]) {
				const v = (row as any)[stat] ?? 0;
				careerTotals[stat] = (careerTotals[stat] ?? 0) + v;
				if (row.season === season) {
					seasonTotals[stat] = (seasonTotals[stat] ?? 0) + v;
				}
			}
		}

		const checks: [Context, Record<string, number>][] = [
			[playoffs ? "seasonPlayoffs" : "season", seasonTotals],
			[playoffs ? "careerPlayoffs" : "career", careerTotals],
		];

		for (const [ctx, totals] of checks) {
			for (const stat of Object.keys(COUNTING) as Stat[]) {
				const value = totals[stat] ?? 0;
				const prev = recs[ctx][stat];

				// Stats added after a league's records were stored have no mark to beat. Seed one
				// quietly from what is actually happening, otherwise a stat that predate the stored
				// records would never be tracked at all. Announcing the first value ever seen would
				// be noise, not news.
				if (prev === undefined) {
					if (value > 0) {
						recs[ctx][stat] = { value, pid: p.pid, name, season };
						changed = true;
					}
					continue;
				}

				if (value <= prev.value) {
					continue;
				}

				const tied = prev.pid === p.pid;
				recs[ctx][stat] = { value, pid: p.pid, name, season };

				// Keep the stored copy in step, or the next session would read the old mark and
				// announce the same record being broken all over again.
				changed = true;

				if (tied) {
					// He already held it; extending your own record every game would spam
					continue;
				}

				await logEvent(
					{
						type: "record",
						text: `<a href="${helpers.leagueUrl(["player", p.pid])}">${name}</a> broke the ${LABELS[ctx]} record for ${COUNTING[stat]} with ${value}, passing ${prev.name}${prev.season ? ` (${prev.season})` : ""}.`,
						showNotification: true,
						extraClass: "notification-record",
						pids: [p.pid],
						tids: [p.tid],
						score: 20,
					},
					conditions,
				);
			}
		}
	}
	if (changed) await save(recs);
};

export default checkRecords;
